import { useState, useMemo } from 'react'
import {
  getOverheads, saveOverhead, getOverheadAllocations, saveOverheadAllocations, getPosSales
} from '../../lib/posStore'
import { useDepartment } from '../../context/DepartmentContext'
import { POS_DEPARTMENTS } from '../../data/posSeed'
import {
  DollarSign, Building, Zap, Users, Percent, Calculator,
  Save, Plus, Trash2, TrendingUp
} from 'lucide-react'

const TABS = [
  { id: 'monthly', label: 'Monthly Overheads' },
  { id: 'allocation', label: 'Department Allocation' },
  { id: 'breakeven', label: 'Breakeven' },
]

const CATEGORIES = [
  { id: 'fixed', label: 'Fixed' },
  { id: 'variable', label: 'Variable' },
]

const FREQUENCIES = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
]

const TRADING_DAYS = 26

function fmt(n) { return `R ${(n || 0).toFixed(2)}` }
function fmtPct(n) { return `${(n || 0).toFixed(1)}%` }

function genId() { return Date.now().toString(36) + Math.random().toString(36).slice(2, 8) }

export default function Overhead() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('monthly')
  const currentMonth = new Date().toISOString().slice(0, 7)
  const [month, setMonth] = useState(currentMonth)
  const [refresh, setRefresh] = useState(0)

  // ── Monthly Overheads state ──
  const [newItem, setNewItem] = useState({ name: '', category: 'fixed', amount: '', frequency: 'monthly' })
  const [editingId, setEditingId] = useState(null)
  const [editAmount, setEditAmount] = useState('')

  // ── Allocation state ──
  const [allocations, setAllocations] = useState(() => getOverheadAllocations(currentDepartmentId))
  const [allocSaved, setAllocSaved] = useState(false)

  // ── Data ──

  const overheads = useMemo(() => {
    void refresh
    return getOverheads(currentDepartmentId, month)
  }, [currentDepartmentId, month, refresh])

  const allOverheads = useMemo(() => {
    void refresh
    return getOverheads(currentDepartmentId)
  }, [currentDepartmentId, refresh])

  const totalMonthly = useMemo(() => {
    return overheads.reduce((sum, o) => {
      const amt = Number(o.amount) || 0
      if (o.frequency === 'weekly') return sum + amt * 4.33
      if (o.frequency === 'daily') return sum + amt * TRADING_DAYS
      return sum + amt
    }, 0)
  }, [overheads])

  const totalAllOverheads = useMemo(() => {
    return allOverheads.reduce((sum, o) => {
      const amt = Number(o.amount) || 0
      if (o.frequency === 'weekly') return sum + amt * 4.33
      if (o.frequency === 'daily') return sum + amt * TRADING_DAYS
      return sum + amt
    }, 0)
  }, [allOverheads])

  const allocSum = useMemo(() => {
    return Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0)
  }, [allocations])

  // ── Handlers: Monthly Overheads ──

  function handleAddItem() {
    if (!newItem.name.trim() || !newItem.amount) return
    saveOverhead({
      name: newItem.name.trim(),
      category: newItem.category,
      amount: Number(newItem.amount) || 0,
      frequency: newItem.frequency,
      month,
      departmentId: currentDepartmentId,
    })
    setNewItem({ name: '', category: 'fixed', amount: '', frequency: 'monthly' })
    setRefresh(r => r + 1)
  }

  function handleSaveEdit(overhead) {
    saveOverhead({ ...overhead, amount: Number(editAmount) || 0, departmentId: currentDepartmentId })
    setEditingId(null)
    setEditAmount('')
    setRefresh(r => r + 1)
  }

  function handleDeleteOverhead(overhead) {
    // Mark as zero / remove from local storage
    saveOverhead({ ...overhead, amount: 0, deleted: true, departmentId: currentDepartmentId })
    setRefresh(r => r + 1)
  }

  // ── Handlers: Allocation ──

  function handleAllocChange(deptId, value) {
    setAllocSaved(false)
    setAllocations(prev => ({ ...prev, [deptId]: Number(value) || 0 }))
  }

  function handleSaveAllocations() {
    saveOverheadAllocations(allocations, currentDepartmentId)
    setAllocSaved(true)
    setTimeout(() => setAllocSaved(false), 2000)
  }

  // ── Breakeven data ──

  const breakevenData = useMemo(() => {
    const allocs = getOverheadAllocations(currentDepartmentId)
    // Get last 30 days of sales
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
    const today = new Date().toISOString().split('T')[0]

    return POS_DEPARTMENTS.map(dept => {
      const deptAlloc = allocs[dept.id] || 0
      const allocatedOverhead = totalAllOverheads * (deptAlloc / 100)
      const dailyOverhead = allocatedOverhead / TRADING_DAYS

      // Get dept sales for last 30 days
      const deptSales = getPosSales(currentDepartmentId, thirtyDaysAgo, today)
      const validSales = deptSales.filter(s => !s.voided)
      const totalRevenue = validSales.reduce((sum, sale) => {
        if (sale.items) {
          return sum + sale.items.reduce((s, item) => s + ((Number(item.price) || 0) * (Number(item.qty) || 1)), 0)
        }
        return sum + (Number(sale.total) || 0)
      }, 0)

      // Count unique sale dates for daily average
      const uniqueDates = new Set(validSales.map(s => s.date))
      const tradingDaysInPeriod = uniqueDates.size || 1
      const dailyAvgRevenue = totalRevenue / tradingDaysInPeriod

      // Breakeven: daily overhead must be covered by daily revenue
      const breakevenRevenue = dailyOverhead
      const aboveBreakeven = dailyAvgRevenue >= breakevenRevenue
      const surplus = dailyAvgRevenue - breakevenRevenue

      return {
        department: dept,
        allocationPct: deptAlloc,
        allocatedOverhead,
        dailyOverhead,
        dailyAvgRevenue,
        breakevenRevenue,
        aboveBreakeven,
        surplus,
        tradingDaysInPeriod,
      }
    })
  }, [currentDepartmentId, totalAllOverheads])

  // ════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-950 px-6 py-5">
        <div className="flex items-center gap-3 mb-1">
          <Building size={22} className="text-amber-400" />
          <h1 className="text-2xl font-bold tracking-tight">Overhead Management</h1>
        </div>
        <p className="text-gray-400 text-sm">Track fixed and variable costs, allocate to departments, and calculate breakeven</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-800 px-6">
        <div className="flex gap-0">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-amber-400 text-amber-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-6 py-6">

        {/* ══════════════════════════════════════ */}
        {/* TAB 1: Monthly Overheads              */}
        {/* ══════════════════════════════════════ */}
        {tab === 'monthly' && (
          <div>
            {/* Month selector */}
            <div className="flex items-center gap-3 mb-6">
              <label className="text-sm text-gray-400">Month:</label>
              <input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="bg-gray-900 border border-gray-800 text-sm text-white px-3 py-2 focus:outline-none focus:border-amber-500"
              />
            </div>

            {/* Add new overhead */}
            <div className="bg-gray-900 border border-gray-800 p-4 mb-6">
              <p className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
                <Plus size={15} className="text-amber-400" />
                Add Overhead Item
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                <input
                  type="text"
                  placeholder="Name (e.g. Rent)"
                  value={newItem.name}
                  onChange={e => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 placeholder:text-gray-600 focus:outline-none focus:border-amber-500"
                />
                <select
                  value={newItem.category}
                  onChange={e => setNewItem(prev => ({ ...prev, category: e.target.value }))}
                  className="bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 focus:outline-none focus:border-amber-500"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Amount (R)"
                  value={newItem.amount}
                  onChange={e => setNewItem(prev => ({ ...prev, amount: e.target.value }))}
                  className="bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 placeholder:text-gray-600 focus:outline-none focus:border-amber-500"
                />
                <select
                  value={newItem.frequency}
                  onChange={e => setNewItem(prev => ({ ...prev, frequency: e.target.value }))}
                  className="bg-gray-950 border border-gray-800 text-sm text-white px-3 py-2 focus:outline-none focus:border-amber-500"
                >
                  {FREQUENCIES.map(f => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
                <button
                  onClick={handleAddItem}
                  disabled={!newItem.name.trim() || !newItem.amount}
                  className="bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:hover:bg-amber-600 text-white text-sm font-semibold px-4 py-2 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={15} />
                  Add
                </button>
              </div>
            </div>

            {/* Overheads table */}
            {overheads.filter(o => !o.deleted).length === 0 ? (
              <div className="bg-gray-900 border border-gray-800 p-10 text-center text-gray-500">
                <DollarSign size={32} className="mx-auto mb-3 opacity-40" />
                <p>No overhead items for {month}. Add items above.</p>
              </div>
            ) : (
              <div className="bg-gray-900 border border-gray-800 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-800">
                      <th className="text-left px-5 py-3 font-medium">Name</th>
                      <th className="text-left px-5 py-3 font-medium">Category</th>
                      <th className="text-right px-5 py-3 font-medium">Amount</th>
                      <th className="text-left px-5 py-3 font-medium">Frequency</th>
                      <th className="text-right px-5 py-3 font-medium">Monthly Equiv.</th>
                      <th className="text-center px-5 py-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overheads.filter(o => !o.deleted).map(o => {
                      const amt = Number(o.amount) || 0
                      const monthlyEquiv = o.frequency === 'weekly' ? amt * 4.33
                        : o.frequency === 'daily' ? amt * TRADING_DAYS
                        : amt
                      const isEditing = editingId === o.id

                      return (
                        <tr key={o.id} className="border-b border-gray-800/50">
                          <td className="px-5 py-2.5 font-medium text-white">{o.name}</td>
                          <td className="px-5 py-2.5">
                            <span className={`text-[11px] font-semibold px-2 py-0.5 border ${
                              o.category === 'fixed'
                                ? 'bg-blue-900/30 text-blue-400 border-blue-800'
                                : 'bg-purple-900/30 text-purple-400 border-purple-800'
                            }`}>
                              {o.category === 'fixed' ? 'Fixed' : 'Variable'}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 text-right">
                            {isEditing ? (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editAmount}
                                  onChange={e => setEditAmount(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(o); if (e.key === 'Escape') setEditingId(null) }}
                                  autoFocus
                                  className="w-28 bg-gray-950 border border-amber-500 text-sm text-white px-2 py-1 text-right focus:outline-none"
                                />
                                <button
                                  onClick={() => handleSaveEdit(o)}
                                  className="text-green-400 hover:text-green-300"
                                >
                                  <Save size={14} />
                                </button>
                              </div>
                            ) : (
                              <span
                                className="text-amber-400 font-semibold cursor-pointer hover:underline"
                                onClick={() => { setEditingId(o.id); setEditAmount(String(o.amount || 0)) }}
                              >
                                {fmt(amt)}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-2.5 text-gray-400 capitalize">{o.frequency}</td>
                          <td className="px-5 py-2.5 text-right text-gray-300">{fmt(monthlyEquiv)}</td>
                          <td className="px-5 py-2.5 text-center">
                            <button
                              onClick={() => handleDeleteOverhead(o)}
                              className="text-gray-600 hover:text-red-400 transition-colors"
                              title="Remove"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-700 font-semibold">
                      <td className="px-5 py-3 text-white" colSpan={4}>Total Monthly Overheads</td>
                      <td className="px-5 py-3 text-right text-amber-400 text-lg">{fmt(totalMonthly)}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 2: Department Allocation           */}
        {/* ══════════════════════════════════════ */}
        {tab === 'allocation' && (
          <div>
            <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Percent size={18} className="text-amber-400" />
                Overhead Allocation by Department
              </h2>
              <p className="text-gray-400 text-sm mb-1">
                Distribute total monthly overheads across departments. Allocations must sum to 100%.
              </p>
              <p className="text-gray-500 text-xs">
                Total overheads to allocate: <span className="text-amber-400 font-semibold">{fmt(totalAllOverheads)}</span>
              </p>
            </div>

            <div className="bg-gray-900 border border-gray-800 p-6">
              <div className="space-y-5">
                {POS_DEPARTMENTS.map(dept => {
                  const pct = allocations[dept.id] || 0
                  const allocatedAmount = totalAllOverheads * (pct / 100)

                  return (
                    <div key={dept.id} className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-3 sm:col-span-2">
                        <p className="text-sm font-medium text-white">{dept.label}</p>
                      </div>
                      <div className="col-span-5 sm:col-span-6">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="1"
                          value={pct}
                          onChange={e => handleAllocChange(dept.id, e.target.value)}
                          className="w-full accent-amber-500 h-2 bg-gray-800 appearance-none cursor-pointer"
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-2">
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={pct}
                            onChange={e => handleAllocChange(dept.id, e.target.value)}
                            className="w-16 bg-gray-950 border border-gray-800 text-sm text-white px-2 py-1 text-right focus:outline-none focus:border-amber-500"
                          />
                          <span className="text-gray-500 text-sm">%</span>
                        </div>
                      </div>
                      <div className="col-span-2 text-right">
                        <p className="text-sm text-amber-400 font-semibold">{fmt(allocatedAmount)}</p>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Sum indicator */}
              <div className="mt-6 pt-4 border-t border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-semibold text-gray-300">
                    Total: <span className={allocSum === 100 ? 'text-green-400' : 'text-red-400'}>{fmtPct(allocSum)}</span>
                  </p>
                  {allocSum !== 100 && (
                    <span className="text-xs text-red-400 flex items-center gap-1">
                      <Zap size={12} />
                      Must equal 100%
                    </span>
                  )}
                  {allocSum === 100 && (
                    <span className="text-xs text-green-400">Balanced</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {allocSaved && (
                    <span className="text-xs text-green-400">Saved</span>
                  )}
                  <button
                    onClick={handleSaveAllocations}
                    disabled={allocSum !== 100}
                    className="bg-amber-600 hover:bg-amber-500 disabled:opacity-40 disabled:hover:bg-amber-600 text-white text-sm font-semibold px-5 py-2 transition-colors flex items-center gap-2"
                  >
                    <Save size={15} />
                    Save Allocations
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════ */}
        {/* TAB 3: Breakeven                       */}
        {/* ══════════════════════════════════════ */}
        {tab === 'breakeven' && (
          <div>
            <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Calculator size={18} className="text-amber-400" />
                Breakeven Analysis by Department
              </h2>
              <p className="text-gray-400 text-sm">
                Each department must generate enough daily revenue to cover its allocated overhead share.
                Based on {TRADING_DAYS} trading days per month.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {breakevenData.map(data => {
                const hasRevenue = data.dailyAvgRevenue > 0
                const hasOverhead = data.dailyOverhead > 0

                return (
                  <div
                    key={data.department.id}
                    className={`bg-gray-900 border p-5 ${
                      !hasOverhead ? 'border-gray-800' :
                      data.aboveBreakeven ? 'border-green-800/50' : 'border-red-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-white">{data.department.label}</h3>
                      {hasOverhead && (
                        <span className={`text-[11px] font-semibold px-2 py-0.5 border ${
                          data.aboveBreakeven
                            ? 'bg-green-900/40 text-green-400 border-green-800'
                            : 'bg-red-900/40 text-red-400 border-red-800'
                        }`}>
                          {data.aboveBreakeven ? 'Above Breakeven' : 'Below Breakeven'}
                        </span>
                      )}
                    </div>

                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400 flex items-center gap-1.5">
                          <Percent size={13} className="text-gray-600" />
                          Allocation
                        </span>
                        <span className="text-gray-300">{fmtPct(data.allocationPct)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 flex items-center gap-1.5">
                          <Building size={13} className="text-gray-600" />
                          Monthly Overhead
                        </span>
                        <span className="text-amber-400 font-semibold">{fmt(data.allocatedOverhead)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400 flex items-center gap-1.5">
                          <DollarSign size={13} className="text-gray-600" />
                          Daily Overhead
                        </span>
                        <span className="text-gray-300">{fmt(data.dailyOverhead)}</span>
                      </div>

                      <div className="border-t border-gray-800 pt-3">
                        <div className="flex justify-between mb-2">
                          <span className="text-gray-400 flex items-center gap-1.5">
                            <TrendingUp size={13} className="text-gray-600" />
                            Breakeven Revenue / Day
                          </span>
                          <span className="text-white font-semibold">{fmt(data.breakevenRevenue)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400 flex items-center gap-1.5">
                            <Users size={13} className="text-gray-600" />
                            Avg Daily Revenue
                          </span>
                          <span className={`font-semibold ${
                            !hasRevenue ? 'text-gray-600' :
                            data.aboveBreakeven ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {hasRevenue ? fmt(data.dailyAvgRevenue) : 'No data'}
                          </span>
                        </div>
                      </div>

                      {hasOverhead && hasRevenue && (
                        <div className={`border-t border-gray-800 pt-3 flex justify-between`}>
                          <span className="text-gray-400">Daily Surplus / Deficit</span>
                          <span className={`font-bold ${data.surplus >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {data.surplus >= 0 ? '+' : ''}{fmt(data.surplus)}
                          </span>
                        </div>
                      )}

                      {!hasRevenue && hasOverhead && (
                        <div className="border-t border-gray-800 pt-3">
                          <p className="text-gray-600 text-xs text-center">No sales data available for this department</p>
                        </div>
                      )}

                      {!hasOverhead && (
                        <div className="border-t border-gray-800 pt-3">
                          <p className="text-gray-600 text-xs text-center">No overhead allocated to this department</p>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Overall Summary */}
            <div className="mt-6 bg-gray-900 border border-gray-800 p-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Total Monthly Overheads</p>
                  <p className="text-xl font-bold text-amber-400">{fmt(totalAllOverheads)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Daily Overhead (Total)</p>
                  <p className="text-xl font-bold text-white">{fmt(totalAllOverheads / TRADING_DAYS)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Departments Above BE</p>
                  <p className="text-xl font-bold text-green-400">
                    {breakevenData.filter(d => d.aboveBreakeven && d.dailyOverhead > 0).length}
                    <span className="text-gray-600 text-sm font-normal"> / {breakevenData.filter(d => d.dailyOverhead > 0).length}</span>
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Trading Days / Month</p>
                  <p className="text-xl font-bold text-white">{TRADING_DAYS}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
